import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Send, X, ExternalLink, AlertCircle } from 'lucide-react';

interface SendFinalNoticeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseData: {
    customerName: string;
    accountNumber: string;
    totalAmountDue: number;
    overdueAmount: number;
    daysPastDue: number;
    invoiceNumbers: string[];
    recipientEmail: string;
  };
  onSendEmail: () => void;
}

export function SendFinalNoticeDialog({
  open,
  onOpenChange,
  caseData,
  onSendEmail,
}: SendFinalNoticeDialogProps) {
  const [isSending, setIsSending] = useState(false);

  const handleSendEmail = async () => {
    setIsSending(true);
    try {
      await onSendEmail();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Generate payment link (this would be a real link in production)
  const paymentLink = `https://payments.example.com/pay/${caseData.accountNumber}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <DialogTitle>Send Final Notice</DialogTitle>
          </div>
          <DialogDescription>
            Review the final notice email before sending to {caseData.recipientEmail}
          </DialogDescription>
        </DialogHeader>

        <Separator className="flex-shrink-0" />

        <div className="px-6 py-4 flex-shrink-0">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This will immediately send the final notice email and bypass any scheduled send time.
            </AlertDescription>
          </Alert>
        </div>

        <ScrollArea className="flex-1 px-6 overflow-y-auto">
          {/* Email Preview */}
          <div className="border rounded-lg bg-card">
            {/* Email Header */}
            <div className="bg-muted/50 px-6 py-4 border-b">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">To:</span>
                  <span className="text-sm text-muted-foreground">{caseData.recipientEmail}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Subject:</span>
                  <span className="text-sm text-muted-foreground">
                    FINAL NOTICE: Payment Required - Account {caseData.accountNumber}
                  </span>
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="px-6 py-6 space-y-6 bg-white text-foreground">
              {/* Greeting */}
              <div>
                <p className="text-base">Dear {caseData.customerName},</p>
              </div>

              {/* Main Message */}
              <div className="space-y-4">
                <p className="text-base font-semibold text-destructive">
                  FINAL NOTICE - IMMEDIATE ACTION REQUIRED
                </p>
                
                <p className="text-base leading-relaxed">
                  This is a final notice regarding your overdue account. Despite our previous attempts to contact you, 
                  your account remains past due and requires immediate attention.
                </p>

                <p className="text-base leading-relaxed">
                  Your account is currently <span className="font-semibold">{caseData.daysPastDue} days past due</span> with 
                  an outstanding balance that must be paid immediately to avoid further collection action.
                </p>
              </div>

              {/* Account Summary */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <h3 className="font-semibold text-base mb-3">Account Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Account Number:</span>
                    <span className="font-medium">{caseData.accountNumber}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Amount Due:</span>
                    <span className="font-semibold text-lg">{formatCurrency(caseData.totalAmountDue)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Overdue Amount:</span>
                    <span className="font-semibold text-destructive">{formatCurrency(caseData.overdueAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Days Past Due:</span>
                    <Badge variant="destructive">{caseData.daysPastDue} days</Badge>
                  </div>
                </div>
              </div>

              {/* Outstanding Invoices */}
              <div className="space-y-2">
                <h3 className="font-semibold text-base">Outstanding Invoices:</h3>
                <div className="flex flex-wrap gap-2">
                  {caseData.invoiceNumbers.map((invoice) => (
                    <Badge key={invoice} variant="outline">
                      {invoice}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Payment Link */}
              <div className="border-2 border-primary rounded-lg p-5 bg-primary/5">
                <h3 className="font-semibold text-base mb-3 text-center">Make a Payment Now</h3>
                <p className="text-sm text-center mb-4 text-muted-foreground">
                  Click the button below to make a secure online payment
                </p>
                <div className="flex justify-center">
                  <Button 
                    className="w-full max-w-md gap-2" 
                    size="lg"
                    onClick={(e) => e.preventDefault()}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Pay {formatCurrency(caseData.overdueAmount)} Now
                  </Button>
                </div>
                <p className="text-xs text-center mt-3 text-muted-foreground">
                  Payment Link: <a href={paymentLink} className="text-primary hover:underline">{paymentLink}</a>
                </p>
              </div>

              {/* Consequences Warning */}
              <div className="border-l-4 border-destructive bg-destructive/10 p-4 rounded-r">
                <h3 className="font-semibold text-base mb-2 text-destructive">Important Notice</h3>
                <p className="text-sm leading-relaxed">
                  Failure to remit payment or contact us within <span className="font-semibold">5 business days</span> may result in:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1 ml-2">
                  <li>Referral to a third-party collection agency</li>
                  <li>Additional collection fees and interest charges</li>
                  <li>Negative impact on your credit rating</li>
                  <li>Legal action to recover the outstanding balance</li>
                </ul>
              </div>

              {/* Contact Information */}
              <div className="space-y-3">
                <p className="text-base leading-relaxed">
                  If you have already made this payment, please disregard this notice and contact us immediately 
                  with your payment confirmation.
                </p>
                
                <p className="text-base leading-relaxed">
                  If you are experiencing financial difficulties, please contact our collections department 
                  immediately to discuss payment arrangements.
                </p>

                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-semibold text-base mb-2">Contact Us</h3>
                  <div className="space-y-1 text-sm">
                    <p>Collections Department</p>
                    <p>Phone: 1-800-COLLECT (265-5328)</p>
                    <p>Email: collections@example.com</p>
                    <p>Hours: Monday - Friday, 8:00 AM - 6:00 PM EST</p>
                  </div>
                </div>
              </div>

              {/* Closing */}
              <div className="space-y-3">
                <p className="text-base">
                  We appreciate your immediate attention to this matter.
                </p>
                <div className="text-base">
                  <p>Sincerely,</p>
                  <p className="font-semibold mt-2">Collections Department</p>
                  <p className="text-sm text-muted-foreground">Your Company Name</p>
                </div>
              </div>

              {/* Footer */}
              <Separator />
              <div className="text-xs text-muted-foreground text-center space-y-1">
                <p>This is an automated collection notice sent on behalf of Your Company Name.</p>
                <p>Please do not reply directly to this email. Use the contact information provided above.</p>
              </div>
            </div>
          </div>
        </ScrollArea>

        <Separator className="flex-shrink-0" />

        <DialogFooter className="px-6 py-4 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={isSending}
            className="gap-2"
          >
            <Send className="h-4 w-4" />
            {isSending ? 'Sending...' : 'Send Final Notice Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
